import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import { Card } from "../../ui/Card";
import { formatMoneyCents, Money } from "../../ui/Money";
import { BillingItemsResponse } from "./types";
import { formatDate } from "./format";

type Props = {
  data: BillingItemsResponse | undefined;
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onPayAll: () => void;
  readOnly?: boolean;
};

// What the patient can pay now, and what their payment plans collect instead
// (memberships audit C37). "Payable now" is the server's figure — the sum of
// what the pay targets accept — so the headline, Pay all and the drawer can
// never offer an amount the payment cannot take. A financed purchase is owed
// but collected by its plan: it is said beside the headline, with the next
// installment, never added to what "Pay all" charges.
//
// "You're all caught up" is said only on a statement that LOADED and owes
// nothing. A failed read used to look exactly like that.
export const HeroSummary = ({
  data,
  isLoading,
  isError = false,
  onRetry,
  onPayAll,
  readOnly = false,
}: Props) => {
  const payableNow = data?.totals?.payableNowCents ?? 0;
  const owed = data?.totals?.outstandingCents ?? 0;
  const onPlan = data?.totals?.onPaymentPlan;
  const byType = data?.totals?.byType ?? {
    appointment: 0,
    membership: 0,
    package: 0,
    fee: 0,
    purchase: 0,
  };

  if (isError && !data) {
    return (
      <Card p={{ base: 5, md: 6 }} mb={4}>
        <Text fontSize="14px" color="#6b7280" mb={1}>
          What you owe
        </Text>
        <Text fontSize="18px" fontWeight="600" color="#b42318">
          We couldn't load your balance.
        </Text>
        <Text fontSize="13px" color="#6b7280" mt={1}>
          Nothing has changed on your account. Try again in a moment.
        </Text>
        {onRetry && (
          <Button mt={3} size="sm" variant="outline" onClick={onRetry} borderRadius="8px">
            Try again
          </Button>
        )}
      </Card>
    );
  }

  const allCaughtUp = !isLoading && Boolean(data) && owed <= 0;
  const planLine =
    onPlan && onPlan.itemCount > 0
      ? `${formatMoneyCents(onPlan.outstandingCents)} on your payment plan` +
        (onPlan.nextInstallmentCents > 0 && onPlan.nextChargeAt
          ? ` — next ${formatMoneyCents(onPlan.nextInstallmentCents)} on ${formatDate(onPlan.nextChargeAt)}`
          : "")
      : null;

  return (
    <Card p={{ base: 5, md: 6 }} mb={4}>
      {allCaughtUp ? (
        <Box>
          <Text fontSize="14px" color="#6b7280" mb={1}>
            What you owe
          </Text>
          <Text
            fontSize={{ base: "24px", md: "30px" }}
            fontWeight="600"
            color="#067647"
            lineHeight="1.1"
          >
            You're all caught up
          </Text>
          <Text fontSize="13px" color="#6b7280" mt={2}>
            We'll let you know when there's something to pay.
          </Text>
        </Box>
      ) : (
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "flex-end" }}
          gap={4}
        >
          <Box>
            <Text fontSize="14px" color="#6b7280" mb={1}>
              To pay now
            </Text>
            <Text
              fontSize={{ base: "32px", md: "40px" }}
              fontWeight="600"
              color="#1a1f36"
              lineHeight="1.1"
            >
              {isLoading ? "—" : <Money cents={payableNow} />}
            </Text>
            {planLine && (
              <Text fontSize="13px" color="#475467" mt={2}>
                {planLine}
              </Text>
            )}
            <HStack
              mt={3}
              spacing={3}
              divider={<Text color="#d1d5db">·</Text>}
              flexWrap="wrap"
              fontSize="13px"
              color="#6b7280"
            >
              <Text>
                {formatMoneyCents(byType.appointment)} appointments
              </Text>
              <Text>{formatMoneyCents(byType.membership)} membership</Text>
              <Text>{formatMoneyCents(byType.package)} packages</Text>
              <Text>{formatMoneyCents(byType.fee)} fees</Text>
              {byType.purchase ? (
                <Text>{formatMoneyCents(byType.purchase)} purchases</Text>
              ) : null}
            </HStack>
          </Box>
          {!readOnly && (
            <Button
              bg="#1a1f36"
              color="white"
              _hover={{ bg: "#2d3348" }}
              onClick={onPayAll}
              isDisabled={isLoading || payableNow <= 0}
              size="md"
              borderRadius="8px"
              fontSize="14px"
              fontWeight="500"
              px={5}
            >
              Pay all
            </Button>
          )}
        </Flex>
      )}
    </Card>
  );
};
